import {
  ToolbarPopoverActionButton,
  ToolbarPopoverButton,
  ToolbarPopoverChip,
  ToolbarPopoverChipGroup,
  ToolbarPopoverSection,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import {
  USER_CARD_STAGE_LABEL,
  USER_CARD_STAGE_ORDER,
  type UserCardStage,
} from "@-/dev.improve.haniverse.constants.user-card-stage";
import type { GraphemeTaiwanUsageClassification } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import {
  areCardFiltersEqual,
  createDefaultCardFilters,
  toggleCardFilterValue,
  type CardFilters,
} from "@-/dev.improve.haniverse.lib.cards.filter";
import {
  getHskLevelLabel,
  getHskLevelsForVersion,
  type HskLevelKey,
} from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import { TBCL_LEVELS, type TbclLevelKey } from "@-/dev.improve.haniverse.lib.morpheme.grade.tbcl";
import type { GraphemeTier } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.data.prc.tier";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import type { Dispatch, ReactNode, SetStateAction } from "react";

const GRAPHEME_TIERS: readonly GraphemeTier[] = [1, 2, 3];

const USAGE_CLASSIFICATION_OPTIONS: readonly {
  label: string;
  value: GraphemeTaiwanUsageClassification;
}[] = [
  { label: "Common", value: "common" },
  { label: "Uncommon", value: "less-than-common" },
  { label: "Rare", value: "rarely-used" },
];

export interface CardFilterMenuProps {
  accessibilityLabel?: string;
  /** Screen-specific extra filter sections appended after the default ones. */
  children?: ReactNode;
  /**
   * The surface's default filter state — what "Reset filters" restores and what the reset button's
   * visibility is compared against. Defaults to the shared `createDefaultCardFilters()`.
   */
  defaultFilters?: CardFilters;
  filters: CardFilters;
  /** Whether a screen-specific filter differs from its default. */
  hasAdditionalFilters?: boolean;
  onFiltersChange: Dispatch<SetStateAction<CardFilters>>;
  onResetAdditionalFilters?: () => void;
}

/**
 * The shared toolbar filter popover: the default set of card filters every toolbar Filters pill
 * offers. Script-aware — it shows HSK levels + tier for the simplified script and TBCL levels +
 * usage classification for the traditional script, plus the learning-stage section. Each
 * any-of-many filter is a row of checkbox chips. Screens own the `CardFilters` state (usually in
 * their layout context) and apply it to their lists with `matchesCardFilters`.
 */
export function CardFilterMenu({
  accessibilityLabel = "Filter cards",
  children,
  defaultFilters,
  filters,
  hasAdditionalFilters = false,
  onFiltersChange,
  onResetAdditionalFilters,
}: CardFilterMenuProps) {
  const resolvedDefaultFilters = defaultFilters ?? createDefaultCardFilters();
  const { hanziScript, hskVersion } = useActorUser();
  const isTraditional = hanziScript === "traditional";
  const hskLevels = getHskLevelsForVersion(hskVersion);

  const toggleHskLevel = (level: HskLevelKey) => {
    onFiltersChange((current) => ({
      ...current,
      hskLevels: toggleCardFilterValue(current.hskLevels, level, hskLevels),
    }));
  };
  const toggleTbclLevel = (level: TbclLevelKey) => {
    onFiltersChange((current) => ({
      ...current,
      tbclLevels: toggleCardFilterValue(current.tbclLevels, level, TBCL_LEVELS),
    }));
  };
  const toggleTier = (tier: GraphemeTier) => {
    onFiltersChange((current) => ({
      ...current,
      tiers: toggleCardFilterValue(current.tiers, tier, GRAPHEME_TIERS),
    }));
  };
  const toggleUsageClassification = (usageClassification: GraphemeTaiwanUsageClassification) => {
    onFiltersChange((current) => ({
      ...current,
      usageClassifications: toggleCardFilterValue(
        current.usageClassifications,
        usageClassification,
        USAGE_CLASSIFICATION_OPTIONS.map((option) => option.value),
      ),
    }));
  };
  const toggleStage = (stage: UserCardStage) => {
    onFiltersChange((current) => ({
      ...current,
      stages: toggleCardFilterValue(current.stages, stage, USER_CARD_STAGE_ORDER),
    }));
  };

  return (
    <ToolbarPopoverButton accessibilityLabel={accessibilityLabel} label="Filters" name="MdTune">
      <ToolbarPopoverSection title={isTraditional ? "TBCL level" : "HSK level"}>
        <ToolbarPopoverChipGroup>
          {isTraditional
            ? TBCL_LEVELS.map((level) => (
                <ToolbarPopoverChip
                  accessibilityRole="checkbox"
                  checked={filters.tbclLevels?.has(level) ?? false}
                  key={level}
                  label={`TBCL ${level}`}
                  onPress={() => toggleTbclLevel(level)}
                  testID={`card-filter-tbcl-${level}`}
                />
              ))
            : hskLevels.map((level) => (
                <ToolbarPopoverChip
                  accessibilityRole="checkbox"
                  checked={filters.hskLevels?.has(level) ?? false}
                  key={level}
                  label={`HSK ${getHskLevelLabel(level)}`}
                  onPress={() => toggleHskLevel(level)}
                  testID={`card-filter-hsk-${level}`}
                />
              ))}
        </ToolbarPopoverChipGroup>
      </ToolbarPopoverSection>
      <ToolbarPopoverSection title={isTraditional ? "Usage" : "Tier"}>
        <ToolbarPopoverChipGroup>
          {isTraditional
            ? USAGE_CLASSIFICATION_OPTIONS.map((option) => (
                <ToolbarPopoverChip
                  accessibilityRole="checkbox"
                  checked={filters.usageClassifications?.has(option.value) ?? false}
                  key={option.value}
                  label={option.label}
                  onPress={() => toggleUsageClassification(option.value)}
                  testID={`card-filter-usage-${option.value}`}
                />
              ))
            : GRAPHEME_TIERS.map((tier) => (
                <ToolbarPopoverChip
                  accessibilityRole="checkbox"
                  checked={filters.tiers?.has(tier) ?? false}
                  key={tier}
                  label={`Tier ${tier}`}
                  onPress={() => toggleTier(tier)}
                  testID={`card-filter-tier-${tier}`}
                />
              ))}
        </ToolbarPopoverChipGroup>
      </ToolbarPopoverSection>
      <ToolbarPopoverSection title="Stage">
        <ToolbarPopoverChipGroup>
          {USER_CARD_STAGE_ORDER.map((stage) => (
            <ToolbarPopoverChip
              accessibilityRole="checkbox"
              checked={filters.stages?.has(stage) ?? false}
              key={stage}
              label={USER_CARD_STAGE_LABEL[stage]}
              onPress={() => toggleStage(stage)}
              testID={`card-filter-stage-${stage}`}
            />
          ))}
        </ToolbarPopoverChipGroup>
      </ToolbarPopoverSection>
      {children}
      {(hasAdditionalFilters || !areCardFiltersEqual(filters, resolvedDefaultFilters)) && (
        <ToolbarPopoverActionButton
          closeOnPress={false}
          label="Reset filters"
          name="MdRefresh"
          onPress={() => {
            onFiltersChange(resolvedDefaultFilters);
            onResetAdditionalFilters?.();
          }}
          testID="card-filter-reset"
        />
      )}
      <ToolbarPopoverActionButton label="Done" name="LuCheck" testID="card-filter-done" />
    </ToolbarPopoverButton>
  );
}
